import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Scale } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { Card } from "../ui/Card";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import { cn } from "../../lib/cn";
import {
  WEIGHT_RANGES,
  hasAnyWeighIn,
  toChartRows,
  type WeightChartPoint,
  type WeightRangeId,
} from "./utils";

const AXIS_TICK = { fontSize: 10, fill: "#617068" };
const GRID_STROKE = "#e5e0d5";
const RAW_COLOR = "#276749";
const TREND_COLOR = "#2f855a";
const GOAL_COLOR = "#617068";
const TRAJECTORY_COLOR = "#0f766e";

function legendItems(trendMature: boolean, hasTrend: boolean, hasForecast: boolean) {
  return [
    { label: "Raw weigh-ins", swatch: "rounded-full", color: RAW_COLOR },
    ...(hasTrend
      ? [{ label: trendMature ? "Trend weight" : "Trend forming", swatch: "rounded-full", color: TREND_COLOR }]
      : []),
    { label: "Planned path", swatch: "rounded-full", color: TRAJECTORY_COLOR },
    { label: "Goal", swatch: "rounded-full", color: GOAL_COLOR },
    ...(hasForecast
      ? [{ label: "Predicted range", swatch: "rounded-sm opacity-30", color: TREND_COLOR }]
      : []),
  ] as const;
}

const TOOLTIP_SERIES_COPY: Record<string, string> = {
  "Raw weigh-in": "Raw weigh-in (actual scale reading)",
  "Trend weight": "Trend weight (smoothed average)",
  "Predicted range": "Predicted range (projection band)",
};

function formatAxisDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatJourneyStartDate(ms: number): string {
  const parts = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).formatToParts(ms);
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  return `${day} ${month}`;
}

function tooltipFormatter(value: unknown, name: unknown): [string, string] {
  if (Array.isArray(value) && value.length === 2) {
    return [
      `${Number(value[0]).toFixed(1)}–${Number(value[1]).toFixed(1)} kg`,
      TOOLTIP_SERIES_COPY["Predicted range"] ?? "Predicted range",
    ];
  }
  const numeric = typeof value === "number" ? value : Number(value);
  const text = Number.isFinite(numeric) ? `${numeric.toFixed(1)} kg` : "—";
  const seriesName = String(name);
  return [text, TOOLTIP_SERIES_COPY[seriesName] ?? seriesName];
}

export interface WeightTrendChartProps {
  readonly rangeId?: WeightRangeId;
  readonly onRangeChange?: (rangeId: WeightRangeId) => void;
  readonly hideRangeControl?: boolean;
}

export function WeightTrendChart({ rangeId: controlledRangeId, onRangeChange, hideRangeControl = false }: WeightTrendChartProps) {
  const [internalRangeId, setInternalRangeId] = useState<WeightRangeId>("30d");
  const rangeId = controlledRangeId ?? internalRangeId;
  const days = WEIGHT_RANGES.find((range) => range.id === rangeId)?.days ?? 30;
  const data = useQuery(api.engine.getWeightChartData, { days });

  useEffect(() => {
    if (data !== undefined) {
      console.debug("[Frontend][WeightTrendChart] Chart data loaded", {
        rangeId,
        days,
        points: data.points.length,
        weighInDayCount: data.weighInDayCount,
        trendMature: data.trendMature,
      });
    }
  }, [data, days, rangeId]);

  const points: WeightChartPoint[] = data?.points ?? [];
  const rows = toChartRows(points);
  const journeyStart = data?.journeyStart ?? null;
  const trendMature = data?.trendMature ?? false;
  const hasTrend = points.some((point) => point.trendKg !== null);
  const hasForecast = points.some((point) => point.predictedMinKg !== null && point.predictedMaxKg !== null);
  const journeyStartChartDate = journeyStart === null
    ? null
    : Math.floor(journeyStart.date / 86_400_000) * 86_400_000;
  const startInsideWindow = journeyStartChartDate !== null && rows.some((row) => row.date === journeyStartChartDate);

  return (
    <Card className="p-4" data-testid="weight-trend">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-text">Weight trend</h3>
          <p className="mt-1 text-sm text-text-muted">Raw weigh-ins, trend line, and projected path to your goal.</p>
        </div>
      </div>
      {!hideRangeControl && (
        <div className="mb-3 flex gap-2" role="tablist" aria-label="Chart range">
          {WEIGHT_RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              role="tab"
              aria-selected={rangeId === range.id}
              data-testid={`weight-range-${range.id}`}
              onClick={() => {
                console.debug("[Frontend][WeightTrendChart] Range changed", { from: rangeId, to: range.id });
                setInternalRangeId(range.id);
                onRangeChange?.(range.id);
              }}
              className={cn(
                "min-h-[44px] flex-1 rounded-xl px-2 text-sm font-semibold transition-all duration-200 ease-out",
                "active:scale-[0.98] select-none",
                rangeId === range.id ? "bg-accent text-white" : "bg-muted text-text-muted",
              )}
            >
              {range.label}
            </button>
          ))}
        </div>
      )}
      {data !== undefined && journeyStart !== null && !startInsideWindow && (
        <p className="mb-2 text-xs font-medium text-text-muted" data-testid="journey-start-context">
          Journey started {formatJourneyStartDate(journeyStart.date)} at {journeyStart.weightKg.toFixed(1)} kg
        </p>
      )}
      {data === undefined ? (
        <Skeleton className="h-[220px] rounded-xl" data-testid="weight-chart-loading" />
      ) : !hasAnyWeighIn(points) ? (
        <EmptyState
          icon={Scale}
          title="No weigh-ins yet"
          message="Log your first weigh-in to start your trend."
          className="py-8"
        />
      ) : (
        <>
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" tickFormatter={formatAxisDate} tick={AXIS_TICK} minTickGap={24} />
                <YAxis domain={["dataMin - 1", "dataMax + 1"]} tick={AXIS_TICK} width={42} />
                <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => formatAxisDate(Number(label))} />
                <Area
                  dataKey="predictedRange"
                  name="Predicted range"
                  stroke="none"
                  fill={TREND_COLOR}
                  fillOpacity={0.15}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="goalKg"
                  name="Goal"
                  stroke={GOAL_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="targetTrajectoryKg"
                  name="Planned path"
                  stroke={TRAJECTORY_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="2 4"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="trendKg"
                  name="Trend weight"
                  stroke={TREND_COLOR}
                  strokeWidth={2.5}
                  strokeDasharray={trendMature ? undefined : "5 4"}
                  strokeOpacity={trendMature ? 1 : 0.6}
                  dot={false}
                  connectNulls
                />
                <Line
                  dataKey="rawKg"
                  name="Raw weigh-in"
                  stroke="none"
                  connectNulls={false}
                  dot={{ r: 3, fill: RAW_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
                {journeyStart !== null && journeyStartChartDate !== null && startInsideWindow && (
                  <ReferenceDot
                    x={journeyStartChartDate}
                    y={journeyStart.weightKg}
                    r={5}
                    fill="#ffffff"
                    stroke={TRAJECTORY_COLOR}
                    strokeWidth={2.5}
                    ifOverflow="extendDomain"
                    aria-label={`Journey start ${journeyStart.weightKg.toFixed(1)} kg`}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="weight-chart-legend">
            {legendItems(trendMature, hasTrend, hasForecast).map((item) => (
              <span key={item.label} className="flex items-center gap-1.5 text-xs text-text-muted">
                <span aria-hidden className={cn("inline-block h-2 w-2", item.swatch)} style={{ backgroundColor: item.color }} />
                {item.label}
              </span>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
